import React, { useState, useEffect, useRef } from 'react';
import { Zap, Clock, CheckCircle, AlertCircle, FileText, Loader2, Camera, Wrench, X, Eye, Upload, Image, Trash2, Send, Truck, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL, api } from '../../utils/api';
import { StatusBadge, PriorityBadge, ServiceIcon, LoadingSpinner } from '../common/Badges';

const TechnicianJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photoForm, setPhotoForm] = useState({ jobId: null, file: null, caption: '', preview: null });
  const [submittingPhoto, setSubmittingPhoto] = useState(false);
  const [jobPhotos, setJobPhotos] = useState({});
  const [logForm, setLogForm] = useState({ jobId: null, description: '' });
  const [submittingLog, setSubmittingLog] = useState(false);
  const [jobLogs, setJobLogs] = useState({});
  const [expandedJob, setExpandedJob] = useState(null);

  useEffect(() => { loadJobs(); }, []);

  const loadJobs = async () => {
    try {
      const data = await api('/api/service-requests');
      setJobs(data);
      for (const job of data) {
        try {
          const [photos, logs] = await Promise.all([
            api(`/api/work-photos?request_id=${job.id}`),
            api(`/api/work-logs?request_id=${job.id}`)
          ]);
          setJobPhotos(prev => ({ ...prev, [job.id]: photos }));
          setJobLogs(prev => ({ ...prev, [job.id]: logs }));
        } catch (e) { console.warn('Failed to load job photos/logs', e); }
      }
    } catch (e) { console.error('Failed to load jobs', e); toast.error('Failed to load jobs'); }
    finally { setLoading(false); }
  };

  const updateStatus = async (jobId, newStatus) => {
    try { await api(`/api/service-requests/${jobId}/status`, { method: 'PUT', body: JSON.stringify({ status: newStatus }) }); toast.success('Status updated'); loadJobs(); }
    catch (err) { toast.error(err.message); }
  };

  const notifyOnTheWay = async (jobId) => {
    const eta = window.prompt('ETA in minutes (optional)', '20');
    try {
      const body = eta && !isNaN(parseInt(eta)) ? JSON.stringify({ eta_minutes: parseInt(eta) }) : '{}';
      await api(`/api/service-requests/${jobId}/notify/on-the-way`, { method: 'POST', body });
      toast.success('Customer notified: On the way');
    } catch (err) { toast.error(err.message); }
  };

  const notifyArrived = async (jobId) => {
    try {
      await api(`/api/service-requests/${jobId}/notify/arrived`, { method: 'POST' });
      toast.success('Customer notified: Arrived');
      loadJobs();
    } catch (err) { toast.error(err.message); }
  };

  const submitDailyReport = async (jobId, isCompleted) => {
    const report = window.prompt(isCompleted ? 'Final summary of completed work:' : "Today's progress (short bullet points):", '');
    if (!report) return;
    try {
      await api(`/api/service-requests/${jobId}/daily-reports`, {
        method: 'POST',
        body: JSON.stringify({ report, status: isCompleted ? 'completed' : 'in_progress' })
      });
      toast.success(isCompleted ? 'Job marked complete' : 'Daily report submitted');
      loadJobs();
    } catch (err) { toast.error(err.message); }
  };

  const uploadPhoto = async (e) => {
    e.preventDefault(); setSubmittingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', photoForm.file);
      const token = localStorage.getItem('token');
      const uploadRes = await fetch(`${API_URL}/api/upload`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      if (!uploadRes.ok) throw new Error('Upload failed');
      const uploadData = await uploadRes.json();
      await api('/api/work-photos', { method: 'POST', body: JSON.stringify({ request_id: photoForm.jobId, image_url: uploadData.url, caption: photoForm.caption }) });
      toast.success('Photo uploaded!'); 
      const photos = await api(`/api/work-photos?request_id=${photoForm.jobId}`);
      setJobPhotos(prev => ({ ...prev, [photoForm.jobId]: photos }));
      setPhotoForm({ jobId: null, file: null, caption: '', preview: null });
    } catch (err) { toast.error(err.message); } finally { setSubmittingPhoto(false); }
  };

  const submitLog = async (e) => {
    e.preventDefault(); setSubmittingLog(true);
    try {
      await api('/api/work-logs', { method: 'POST', body: JSON.stringify({ request_id: logForm.jobId, service_performed: 'Work Update', description: logForm.description }) });
      toast.success('Work log sent!');
      const logs = await api(`/api/work-logs?request_id=${logForm.jobId}`);
      setJobLogs(prev => ({ ...prev, [logForm.jobId]: logs }));
      setLogForm({ jobId: null, description: '' });
    } catch (err) { toast.error(err.message); } finally { setSubmittingLog(false); }
  };

  if (loading) return <LoadingSpinner />;

  const activeJobs = jobs.filter(j => ['assigned', 'in_progress'].includes(j.status));
  const doneJobs = jobs.filter(j => j.status === 'completed');

  return (
    <div data-testid="technician-jobs" className="space-y-6">
      <div>
        <p className="text-xs text-red-500 font-bold tracking-[0.3em] mb-1">MY ASSIGNMENTS</p>
        <h1 className="font-heading text-3xl font-bold text-white">Jobs</h1>
      </div>

      {/* Active Jobs */}
      {activeJobs.length === 0 && doneJobs.length === 0 && (
        <div className="rounded-lg bg-gray-900 border border-gray-800 p-12 text-center">
          <Wrench className="w-10 h-10 mx-auto mb-3 text-gray-700" />
          <p className="text-gray-500">No jobs assigned yet.</p>
        </div>
      )}

      {activeJobs.map((job) => (
        <div key={job.id} className="rounded-lg bg-gray-900 border border-gray-800 overflow-hidden" data-testid={`job-card-${job.id}`}>
          {/* Job Header */}
          <div className="px-5 py-4 border-b border-gray-800 flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <p className="font-mono text-xs text-gray-600">{job.ticket_number}</p>
                <StatusBadge status={job.status} />
                <PriorityBadge priority={job.priority} />
              </div>
              <p className="text-lg font-bold text-white">
                <ServiceIcon type={job.service_type} className="w-5 h-5 inline mr-2" />
                {job.service_type.replace('_', ' ').toUpperCase()}
              </p>
              <p className="text-sm text-gray-400 mt-1">{job.description}</p>
              {job.address && <p className="text-xs text-gray-600 mt-1">{job.address}</p>}
              <p className="text-xs text-gray-500 mt-1">Customer: {job.customer_name}</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="px-5 py-3 bg-gray-900/50 flex flex-wrap gap-2 border-b border-gray-800/50">
            {job.status === 'assigned' && (
              <>
                <button onClick={() => notifyOnTheWay(job.id)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all" data-testid={`notify-on-the-way-btn-${job.id}`}>
                  <Truck className="w-4 h-4" />
                  On the Way
                </button>
                <button onClick={() => notifyArrived(job.id)} className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all" data-testid={`notify-arrived-btn-${job.id}`}>
                  <MapPin className="w-4 h-4" />
                  I&apos;ve Arrived
                </button>
                <button onClick={() => updateStatus(job.id, 'in_progress')} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all" data-testid={`accept-job-btn-${job.id}`}>
                  <CheckCircle className="w-4 h-4" />
                  Start Job
                </button>
              </>
            )}
            {job.status === 'in_progress' && (
              <button onClick={() => updateStatus(job.id, 'completed')} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all" data-testid={`complete-job-btn-${job.id}`}>
                <CheckCircle className="w-4 h-4" />
                Mark Complete
              </button>
            )}
            <button onClick={() => setLogForm({ jobId: job.id, description: '' })} className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all border border-gray-700" data-testid={`add-log-btn-${job.id}`}>
              <FileText className="w-4 h-4" />
              Write Update
            </button>
            <button onClick={() => setPhotoForm({ jobId: job.id, file: null, caption: '', preview: null })} className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all border border-gray-700" data-testid={`upload-photo-btn-${job.id}`}>
              <Camera className="w-4 h-4" />
              Upload Photo
            </button>
            <button onClick={() => submitDailyReport(job.id, false)} className="bg-blue-700 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all" data-testid={`daily-report-btn-${job.id}`}>
              <FileText className="w-4 h-4" />
              Daily Report
            </button>
            <button onClick={() => { if (window.confirm('Mark this job as COMPLETED?')) submitDailyReport(job.id, true); }} className="bg-green-700 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all" data-testid={`mark-complete-btn-${job.id}`}>
              <CheckCircle className="w-4 h-4" />
              Mark Complete
            </button>
            <button onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)} className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all border border-gray-700" data-testid={`toggle-history-btn-${job.id}`}>
              <Eye className="w-4 h-4" />
              History ({(jobLogs[job.id] || []).length + (jobPhotos[job.id] || []).length})
            </button>
          </div>

          {/* Expanded History */}
          {expandedJob === job.id && (
            <div className="px-5 py-4 bg-gray-950/50">
              {(jobLogs[job.id] || []).length === 0 && (jobPhotos[job.id] || []).length === 0 && (
                <p className="text-sm text-gray-600 text-center py-4">No work logs or photos yet.</p>
              )}

              {(jobLogs[job.id] || []).map((log) => (
                <div key={log.id} className="flex gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
                    <FileText className="w-4 h-4 text-blue-400" />
                  </div>
                  <div className="flex-1 bg-gray-800/50 rounded-lg p-3">
                    <p className="text-sm text-white">{log.description}</p>
                    <p className="text-xs text-gray-600 mt-1">{log.service_performed} {log.created_at ? `- ${new Date(log.created_at).toLocaleString()}` : ''}</p>
                  </div>
                </div>
              ))}

              {(jobPhotos[job.id] || []).length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2">Photos</p>
                  <div className="grid grid-cols-4 gap-2">
                    {jobPhotos[job.id].map((photo) => (
                      <img key={photo.id} src={photo.image_url} alt={photo.caption || ''} className="w-full h-20 object-cover rounded-lg border border-gray-700" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Completed Jobs */}
      {doneJobs.length > 0 && (
        <div className="rounded-lg bg-gray-900 border border-gray-800 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-800">
            <h2 className="text-sm font-bold text-white tracking-wide">Completed ({doneJobs.length})</h2>
          </div>
          <div className="divide-y divide-gray-800/50">
            {doneJobs.map((job) => (
              <div key={job.id} className="px-5 py-3 flex items-center justify-between opacity-60" data-testid={`done-job-${job.id}`}>
                <div>
                  <p className="font-mono text-xs text-gray-600">{job.ticket_number}</p>
                  <p className="text-sm text-white">{job.service_type.replace('_', ' ').toUpperCase()}</p>
                </div>
                <CheckCircle className="w-4 h-4 text-green-500" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Photo Modal */}
      {photoForm.jobId && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-md w-full" data-testid="upload-photo-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Upload Work Photo</h3>
              <button onClick={() => setPhotoForm({ jobId: null, file: null, caption: '', preview: null })} className="p-1.5 rounded-lg hover:bg-gray-800"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <form onSubmit={uploadPhoto} className="space-y-4">
              <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center hover:border-red-500 transition-colors cursor-pointer relative">
                <input
                  type="file"
                  accept="image/*,.heic,.heif,.pdf,.doc,.docx"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
                      setPhotoForm({ ...photoForm, file, preview });
                    }
                  }}
                  data-testid="photo-file-input"
                />
                {photoForm.preview ? (
                  <img src={photoForm.preview} alt="Preview" className="w-full h-40 object-cover rounded" />
                ) : (
                  <div>
                    <Upload className="w-10 h-10 mx-auto mb-2 text-gray-600" />
                    <p className="text-sm text-gray-400">Tap to take or upload photo</p>
                  </div>
                )}
              </div>
              <input type="text" placeholder="Caption (optional)" className="input" value={photoForm.caption} onChange={(e) => setPhotoForm({ ...photoForm, caption: e.target.value })} data-testid="photo-caption-input" />
              <button type="submit" disabled={submittingPhoto || !photoForm.file} className="btn-primary w-full py-3 font-bold" data-testid="submit-photo-btn">{submittingPhoto && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}Upload Photo</button>
            </form>
          </div>
        </div>
      )}

      {/* Work Log Modal - Simple */}
      {logForm.jobId && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-md w-full" data-testid="work-log-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Write Work Update</h3>
              <button onClick={() => setLogForm({ jobId: null, description: '' })} className="p-1.5 rounded-lg hover:bg-gray-800"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <form onSubmit={submitLog} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">What did you work on today?</label>
                <textarea className="input min-h-[120px]" placeholder="Describe the work done..." value={logForm.description} onChange={(e) => setLogForm({ ...logForm, description: e.target.value })} required data-testid="work-description-input" />
              </div>
              <button type="submit" disabled={submittingLog} className="btn-primary w-full py-3 font-bold" data-testid="submit-log-btn">{submittingLog && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}Send Update</button>
              <p className="text-xs text-gray-600 text-center">This update will be visible to the customer in real-time</p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};


export default TechnicianJobs;
